import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useLocation } from "wouter";
import {
  ChevronLeft, ChevronRight, Check, X, Loader2,
  Shield, Wrench, Car, FileText, MapPin, Phone, Users, RefreshCw,
  MessageCircle, ClipboardList, Upload, AlertCircle, Plus, Minus,
  Camera, SkipForward
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

// ─── Types ────────────────────────────────────────────────────────────────────
type KmPrices = { price36: number | null; price24: number | null; price12: number | null };

interface Vehicle {
  id: string | number;
  zohoId?: string;
  make: string;
  model: string;
  variant: string;
  type: string;
  transmission: string;
  fuel: string;
  seats: number;
  fromPrice: number;
  // Legacy flat fields (default km pack)
  rate36?: number | null;
  rate24?: number | null;
  rate12?: number | null;
  // Consolidated km pricing map
  kmPricing?: Record<number, KmPrices>;
  availableKmPacks?: number[];
  km?: number;
  desc: string;
  image: string;
  status?: string;
}

type Term = 12 | 24 | 36;
type KmPack = 1500 | 2000 | 2500;
type Modal = "none" | "contact" | "apply";
type ApplyStep = 1 | 2 | 3 | 4;
type DocMode = "upload" | "scan";

// ─── Pricing helpers ──────────────────────────────────────────────────────────
function getRateForKmAndTerm(vehicle: Vehicle, km: number, term: Term): number | null {
  // Use consolidated kmPricing map if available
  if (vehicle.kmPricing && vehicle.kmPricing[km]) {
    const p = vehicle.kmPricing[km];
    if (term === 12) return p.price12 ?? null;
    if (term === 24) return p.price24 ?? null;
    if (term === 36) return p.price36 ?? null;
  }
  // Fallback to legacy flat fields
  const r12 = vehicle.rate12 ?? null;
  const r24 = vehicle.rate24 ?? null;
  const r36 = vehicle.rate36 ?? null;
  if (term === 12) return r12;
  if (term === 24) return r24;
  if (term === 36) return r36 ?? (r24 ? Math.round(r24 * 0.97) : null);
  return r12;
}

function getAvailableTermsForKm(vehicle: Vehicle, km: number): Term[] {
  const terms: Term[] = [];
  if (vehicle.kmPricing && vehicle.kmPricing[km]) {
    const p = vehicle.kmPricing[km];
    if (p.price12) { terms.push(12); }
    if (p.price24) terms.push(24);
    if (p.price36) terms.push(36);
  } else {
    if (vehicle.rate12) { terms.push(12); }
    if (vehicle.rate24) terms.push(24);
    if (vehicle.rate36) terms.push(36);
  }
  return terms.length > 0 ? terms : [12];
}

const ADDITIONAL_DRIVER_FEE = 299;
const DELIVERY_FEE = 1500;
const KM_SURCHARGE_2000 = 350;
const KM_SURCHARGE_2500 = 700;

// ─── SA ID helpers ────────────────────────────────────────────────────────────
function validateSAID(id: string): { valid: boolean; error: string } {
  if (!/^\d{13}$/.test(id)) return { valid: false, error: "ID number must be exactly 13 digits." };
  const yy = parseInt(id.substring(0, 2), 10);
  const mm = parseInt(id.substring(2, 4), 10);
  const dd = parseInt(id.substring(4, 6), 10);
  if (mm < 1 || mm > 12) return { valid: false, error: "ID number contains an invalid month." };
  if (dd < 1 || dd > 31) return { valid: false, error: "ID number contains an invalid day." };
  const citizenship = parseInt(id[10], 10);
  if (citizenship !== 0 && citizenship !== 1) return { valid: false, error: "ID number has an invalid citizenship digit." };
  let sum = 0;
  for (let i = 0; i < 13; i++) {
    let digit = parseInt(id[i], 10);
    if (i % 2 === 1) { digit *= 2; if (digit > 9) digit -= 9; }
    sum += digit;
  }
  if (sum % 10 !== 0) return { valid: false, error: "ID number is invalid (checksum failed)." };
  return { valid: true, error: "" };
}

function dobFromSAID(id: string): string {
  if (id.length < 6) return "";
  const yy = parseInt(id.substring(0, 2), 10);
  const mm = id.substring(2, 4);
  const dd = id.substring(4, 6);
  const currentYY = new Date().getFullYear() % 100;
  const yyyy = yy <= currentYY ? 2000 + yy : 1900 + yy;
  return `${yyyy}-${mm}-${dd}`;
}

// ─── Bank finance estimator ───────────────────────────────────────────────────
function getBankFinanceEstimate(rrp: number, kmPerMonth: number = 1500) {
  const deposit = Math.round(rrp * 0.1);
  const loanAmount = rrp - deposit;
  const annualRate = 0.1275;
  const monthlyRate = annualRate / 12;
  const loanTermMonths = 60;
  const monthlyPayment = Math.round(
    (loanAmount * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -loanTermMonths))
  );
  // Insurance: 6.5% of RRP per annum, divided by 12
  const insurance = Math.round((rrp * 0.065) / 12);
  const tracker = 299;
  const tyres = Math.round(kmPerMonth * 0.25);
  const maintenance = Math.round(kmPerMonth * 0.20);
  const licensing = Math.round((rrp * 0.0015) / 12);
  const initiationFee = 5000;
  const onRoadCosts = 6000;
  return {
    monthlyVehicle: monthlyPayment,
    insurance, tracker, tyres, maintenance, licensing,
    totalMonthly: monthlyPayment + insurance + tracker + tyres + maintenance + licensing,
    deposit, initiationFee, onRoadCosts,
    totalUpfront: deposit + initiationFee + onRoadCosts,
  };
}

// ─── Inclusions ───────────────────────────────────────────────────────────────
const inclusions = [
  { icon: Shield,    title: "Comprehensive Insurance",       desc: "Fully comprehensive cover with an agreed excess — built into your monthly fee." },
  { icon: Wrench,    title: "Routine Services & Maintenance", desc: "Scheduled services and approved maintenance are covered, so you stay on the road." },
  { icon: Car,       title: "Tyre & Wear-and-Tear Allowance", desc: "Reasonable wear-and-tear support built into your plan, depending on mileage." },
  { icon: FileText,  title: "Licence & Registration",         desc: "Licensing, registration and admin are handled for you — no queueing or paperwork." },
  { icon: MapPin,    title: "Tracker & Recovery",             desc: "All vehicles include tracking and stolen-vehicle recovery for added peace of mind." },
  { icon: Phone,     title: "24/7 Roadside Assist",           desc: "Towing, breakdown and roadside support — available around the clock." },
  { icon: Users,     title: "Add Additional Drivers",         desc: "Add approved additional drivers to your subscription, where your plan allows." },
  { icon: RefreshCw, title: "Flexible Change Options",        desc: "When life changes, talk to us about upgrading, downgrading or changing your plan." },
];

const bankCons = [
  "60–84 month term is common.",
  "Deposit often required, plus on-road costs.",
  "Insurance, maintenance, tyres and licensing are all extra.",
  "Balloon payments and settlement risk if you want to exit early.",
  "Harder to change cars quickly when your life changes.",
];
const subscriptionPros = [
  "12–36 month terms, not 5–7 years.",
  "No balloon payments and no long-term debt on your name.",
  "Insurance, services, licensing, roadside assist and tracking included.",
  "Upgrade or switch when your needs change, subject to your contract.",
  "One predictable monthly payment you can plan for.",
];

// ─── Shared styles ────────────────────────────────────────────────────────────
const inputCls = "w-full px-3 py-2.5 rounded-lg text-sm outline-none";
const inputStyle = { border: "1px solid rgba(46,0,79,0.2)", color: "#2e004f", backgroundColor: "#fff" };
const labelStyle: React.CSSProperties = { display: "block", fontSize: "0.75rem", fontWeight: 600, color: "#2e004f", marginBottom: "0.375rem" };

// ─── Contact Me Modal ─────────────────────────────────────────────────────────
function ContactModal({ vehicle, term, rateForTerm, onClose }: {
  vehicle: Vehicle; term: Term; rateForTerm: number; onClose: () => void;
}) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [vehicleField, setVehicleField] = useState("");
  const [description, setDescription] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [contactMethod, setContactMethod] = useState("");

  const vehicleName = `${vehicle.make} ${vehicle.model} ${vehicle.variant}`;

  useEffect(() => {
    setVehicleField(`${vehicleName} — ${term}-month`);
    setFirstName(""); setLastName(""); setPhone(""); setEmail(""); setDescription(""); setConsent(false);
    setContactMethod("");
    setSubmitted(false); setFormError("");
  }, [vehicle, term]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");
    if (!firstName.trim()) { setFormError("First Name cannot be empty."); return; }
    if (!lastName.trim()) { setFormError("Last Name cannot be empty."); return; }
    if (!phone.trim()) { setFormError("Phone number cannot be empty."); return; }
    if (!contactMethod) { setFormError("Please select how we should reach you."); return; }
    if (!consent) { setFormError("Please accept the consent checkbox."); return; }
    if (!email.trim()) { setFormError("Email address is required."); return; }
    const at = email.indexOf("@"); const dot = email.lastIndexOf(".");
    if (at < 1 || dot < at + 2 || dot + 2 >= email.length) { setFormError("Please enter a valid email address."); return; }
    setSubmitting(true);
    try {
      // POST to /api/leads — server handles both Zoho WebToLeadForm and portal logging
      fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          formType: "Contact Me",
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          phone: phone.trim(),
          contactMethod,
          vehicleOfInterest: vehicleField.trim() || vehicleName,
          vehicleInMind: vehicleName,
          description: description.trim(),
        }),
      }).catch(() => {});
      setSubmitted(true);
    } catch {
      setFormError("Something went wrong. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative rounded-xl overflow-hidden w-full max-h-[90vh] overflow-y-auto shadow-2xl" style={{ backgroundColor: "#ffffff", maxWidth: "560px", border: "1px solid rgba(46,0,79,0.12)" }} onClick={(e) => e.stopPropagation()}>
        <div className="px-6 pt-5 pb-4 sticky top-0 z-10 border-b" style={{ backgroundColor: "#2e004f", borderColor: "rgba(255,255,255,0.1)" }}>
          <h3 className="text-lg font-semibold text-white">Contact Me — {vehicle.make} {vehicle.model}</h3>
          <p className="text-white/60 text-sm mt-0.5">From <span className="text-orange-300 font-semibold">R{rateForTerm.toLocaleString()} pm</span> · {vehicle.variant}</p>
          <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-all"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6">
          {submitted ? (
            <div className="flex flex-col items-center justify-center text-center py-8 gap-4">
              <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(230,83,30,0.1)" }}>
                <Check className="w-7 h-7" style={{ color: "#e6531e" }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: "#2e004f" }}>Thank you, {firstName}!</h3>
              <p className="text-sm max-w-xs" style={{ color: "#5a3d70" }}>We've received your enquiry and a member of our team will be in touch within 24 hours.</p>
              <p className="text-xs font-medium px-4 py-2 rounded-full" style={{ backgroundColor: "rgba(230,83,30,0.08)", color: "#e6531e" }}>Vehicle: {vehicleName} — {term}-month</p>
              <button onClick={onClose} className="mt-2 w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-white" style={{ backgroundColor: "#2e004f" }}>Close</button>
            </div>
          ) : (
            <>
              <div className="mb-5 px-4 py-3 rounded-lg border" style={{ backgroundColor: "rgba(230,83,30,0.06)", borderColor: "rgba(230,83,30,0.25)" }}>
                <p className="text-xs font-semibold uppercase tracking-wider mb-0.5" style={{ color: "#e6531e" }}>Vehicle of Interest</p>
                <p className="text-sm font-medium" style={{ color: "#2e004f" }}>{vehicleName} — {term}-month subscription</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div className="grid grid-cols-2 gap-4">
                  <div><label style={labelStyle}>First Name *</label><input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} className={inputCls} style={inputStyle} placeholder="First name" maxLength={40} /></div>
                  <div><label style={labelStyle}>Last Name *</label><input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} className={inputCls} style={inputStyle} placeholder="Last name" maxLength={80} /></div>
                </div>
                <div><label style={labelStyle}>Phone Number *</label><input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} style={inputStyle} placeholder="082 000 0000" maxLength={30} /></div>
                <div>
                  <label style={labelStyle}>How should we reach you? *</label>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    {(["Phone Call", "WhatsApp", "Email"] as const).map((opt) => (
                      <button key={opt} type="button" onClick={() => setContactMethod(opt)}
                        className="py-2 px-3 rounded-lg text-sm font-medium border transition-all"
                        style={{ backgroundColor: contactMethod === opt ? "#e6531e" : "#fff", color: contactMethod === opt ? "#fff" : "#2e004f", borderColor: contactMethod === opt ? "#e6531e" : "rgba(46,0,79,0.2)" }}>
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
                <div><label style={labelStyle}>Email Address</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} style={inputStyle} placeholder="you@example.com" maxLength={100} /></div>
                <div><label style={labelStyle}>Vehicle of Interest</label><input type="text" value={vehicleField} onChange={(e) => setVehicleField(e.target.value)} className={inputCls} style={inputStyle} maxLength={255} /></div>
                <div><label style={labelStyle}>Comments</label><textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className={`${inputCls} resize-none`} style={inputStyle} placeholder="Any questions or requirements..." /></div>
                <div className="flex items-start gap-2">
                  <input type="checkbox" id="detail_consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-orange-500" />
                  <label htmlFor="detail_consent" className="text-xs" style={{ color: "#5a3d70" }}>I agree to be contacted by RideCloud regarding this enquiry.</label>
                </div>
                {formError && <p className="text-sm font-medium" style={{ color: "#c0392b" }}>{formError}</p>}
                <button type="submit" disabled={submitting} className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-white" style={{ backgroundColor: "#e6531e", opacity: submitting ? 0.7 : 1 }}>
                  {submitting ? "Sending..." : <>{"Send Enquiry"} <ChevronRight className="w-4 h-4" /></>}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Apply Now Modal ──────────────────────────────────────────────────────────
function ApplyModal({ vehicle, term, rateForTerm, kmPack, additionalDrivers, includeDelivery, onClose }: {
  vehicle: Vehicle; term: Term; rateForTerm: number; kmPack: KmPack; additionalDrivers: number; includeDelivery: boolean; onClose: () => void;
}) {
  const [step, setStep] = useState<ApplyStep>(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [attempted, setAttempted] = useState(false);

  // Step 1 — Personal info
  const [personal, setPersonal] = useState({
    firstName: "", lastName: "", email: "", phone: "",
    idNumber: "", dateOfBirth: "", address: "",
    employmentStatus: "", monthlyIncome: "",
    contactMethod: "",
  });
  const [idError, setIdError] = useState("");

  // Step 2 — Documents (dual: SA ID + Driver's Licence, each independent)
  const [idFile, setIdFile] = useState<File | null>(null);
  const [licenceFile, setLicenceFile] = useState<File | null>(null);
  const [idMode, setIdMode] = useState<DocMode>("upload");
  const [licenceMode, setLicenceMode] = useState<DocMode>("upload");
  const [idValidating, setIdValidating] = useState(false);
  const [licenceValidating, setLicenceValidating] = useState(false);
  const [idValidation, setIdValidation] = useState<{ ok: boolean; message: string } | null>(null);
  const [licenceValidation, setLicenceValidation] = useState<{ ok: boolean; message: string } | null>(null);
  const [idDocUrl, setIdDocUrl] = useState<string | null>(null);
  const [licenceDocUrl, setLicenceDocUrl] = useState<string | null>(null);
  const [docSkipped, setDocSkipped] = useState(false);
  const idFileInputRef = useRef<HTMLInputElement>(null);
  const licenceFileInputRef = useRef<HTMLInputElement>(null);
  const idCameraInputRef = useRef<HTMLInputElement>(null);
  const licenceCameraInputRef = useRef<HTMLInputElement>(null);
  const modalScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => { if (modalScrollRef.current) { modalScrollRef.current.scrollTop = 0; } }, [step]);

  // Step 3 — Consent
  const [creditConsent, setCreditConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);

  const vehicleName = `${vehicle.make} ${vehicle.model} ${vehicle.variant}`;
  // rateForTerm already includes km pricing (no surcharge needed)
  const driverFee = additionalDrivers * ADDITIONAL_DRIVER_FEE;
  const totalMonthly = rateForTerm + driverFee;

  // Steps: 4 steps, "Consent" renamed to "Review" (step 3 = consent, step 4 = review & submit)
  const steps = [
    { num: 1, label: "Personal Info" },
    { num: 2, label: "Documents" },
    { num: 3, label: "Consent" },
    { num: 4, label: "Review" },
  ];

  const validateStep = () => {
    if (step === 1) {
      if (!personal.firstName.trim()) return "Please enter your first name.";
      if (!personal.lastName.trim()) return "Last name is required.";
      if (!personal.email.trim() || !personal.email.includes("@")) return "A valid email address is required.";
      if (!personal.phone.trim()) return "Phone number is required.";
      if (!personal.contactMethod) return "Please select how we should reach you.";
      if (!personal.idNumber.trim()) return "SA ID number is required.";
      const idCheck = validateSAID(personal.idNumber);
      if (!idCheck.valid) return idCheck.error;
    }
    // Step 2: document is optional (can skip)
    if (step === 3 && !creditConsent) return "You must consent to a credit check to proceed.";
    return "";
  };

  const handleNext = () => {
    setAttempted(true);
    const err = validateStep();
    if (err) { setError(err); return; }
    setError("");
    setAttempted(false);
    setStep((s) => (s + 1) as ApplyStep);
  };

  // Document validation via AI
  const validateDocument = async (file: File, docType: "id" | "licence") => {
    const setValidating = docType === "id" ? setIdValidating : setLicenceValidating;
    const setValidation = docType === "id" ? setIdValidation : setLicenceValidation;
    setValidating(true);
    setValidation(null);
    try {
      // Convert file to base64 for JSON transport (avoids multipart parsing on serverless)
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // Strip the data URL prefix (e.g. "data:image/jpeg;base64,")
          resolve(result.split(",")[1] || result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await fetch("/api/validate-document", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ base64, mimeType: file.type, docType, fileName: file.name }),
      });
      if (res.ok) {
        const data = await res.json();
        setValidation({ ok: data.valid, message: data.message });
        // Store the Supabase Storage URL returned by the server
        if (data.docUrl) {
          if (docType === "id") setIdDocUrl(data.docUrl);
          else setLicenceDocUrl(data.docUrl);
        }
      } else {
        setValidation({ ok: false, message: "Validation failed. Please try again." });
      }
    } catch {
      setValidation({ ok: true, message: "Could not validate automatically — our team will review." });
    } finally {
      setValidating(false);
    }
  };

  // Camera capture handler
  const handleCameraCapture = (docType: "id" | "licence") => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (docType === "id") { setIdFile(file); setIdValidation(null); validateDocument(file, "id"); }
    else { setLicenceFile(file); setLicenceValidation(null); validateDocument(file, "licence"); }
  };

  const handleSubmit = async () => {
    setError(""); setSubmitting(true);
    try {
      const docParts: string[] = [];
      if (idFile) docParts.push(`SA ID: ${idFile.name}`);
      if (licenceFile) docParts.push(`Driver's Licence: ${licenceFile.name}`);
      const docSummary = docSkipped ? "Skipped" : docParts.length > 0 ? docParts.join(" | ") : "None uploaded";

      const vehicleLabel = `${vehicleName} — ${term}-month @ R${totalMonthly.toLocaleString()} pm`;
      const termLabel = `${term} Months`;
      const kmPackNum = Number(kmPack);
      const kmLabel = kmPackNum === 1500 ? "1,500 km/month" : kmPackNum === 2000 ? "2,000 km/month" : "2,500 km/month";

      // POST to /api/leads — server handles both Zoho WebToLeadForm and portal logging
      fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          formType: "Apply Now",
          firstName: personal.firstName,
          lastName: personal.lastName,
          email: personal.email,
          phone: personal.phone,
          contactMethod: personal.contactMethod,
          vehicleOfInterest: vehicleLabel,
          vehicleInMind: vehicleName,
          subscriptionTerm: termLabel,
          kmPack: kmLabel,
          idNumber: personal.idNumber,
          dateOfBirth: personal.dateOfBirth,
          address: personal.address,
          employmentStatus: personal.employmentStatus,
          monthlyIncome: personal.monthlyIncome,
          additionalDrivers: String(additionalDrivers),
          includeDelivery: includeDelivery,
          creditConsent: creditConsent,
          description: `Docs: ${docSummary}`,
          idDocUrl: idDocUrl || undefined,
          licenceDocUrl: licenceDocUrl || undefined,
        }),
      }).catch(() => {});
      setSubmitted(true);
    } catch { setError("Network error. Please try again."); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        ref={modalScrollRef}
        className="relative rounded-xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        style={{ backgroundColor: "#ffffff", maxWidth: "600px", border: "1px solid rgba(46,0,79,0.12)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Modal header + stepper ── */}
        <div className="px-6 pt-5 pb-4 sticky top-0 z-10" style={{ backgroundColor: "#2e004f" }}>
          <h3 className="text-lg font-semibold text-white">Apply Now — {vehicle.make} {vehicle.model}</h3>
          <p className="text-white/60 text-sm mt-0.5">
            {vehicle.variant} · {term}-month · <span className="text-orange-300 font-semibold">R{totalMonthly.toLocaleString()} pm</span>
          </p>
          <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-all">
            <X className="w-4 h-4" />
          </button>

          {/* ── Stepper — equal spacing ── */}
          {!submitted && (
            <div className="flex items-center mt-4">
              {steps.map((s, i) => (
                <div key={s.num} className="flex items-center" style={{ flex: 1 }}>
                  {/* Step dot + label */}
                  <div className="flex flex-col items-center" style={{ flex: "0 0 auto", minWidth: 0 }}>
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all"
                      style={
                        step > s.num
                          ? { backgroundColor: "#16a34a", color: "#fff" }
                          : step === s.num
                          ? { backgroundColor: "#e6531e", color: "#fff" }
                          : { backgroundColor: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.5)" }
                      }
                    >
                      {step > s.num ? <Check className="w-3.5 h-3.5" /> : s.num}
                    </div>
                    <span
                      className="text-xs mt-1 hidden sm:block text-center"
                      style={{ color: step === s.num ? "#fff" : "rgba(255,255,255,0.4)", whiteSpace: "nowrap" }}
                    >
                      {s.label}
                    </span>
                  </div>
                  {/* Connector line between steps */}
                  {i < steps.length - 1 && (
                    <div
                      className="h-px mb-5"
                      style={{
                        flex: 1,
                        marginLeft: "6px",
                        marginRight: "6px",
                        backgroundColor: step > s.num ? "#16a34a" : "rgba(255,255,255,0.2)",
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Modal body ── */}
        <div className="p-6">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: "rgba(22,163,74,0.1)" }}>
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ color: "#2e004f" }}>Application Submitted!</h3>
              <p className="text-sm mb-6" style={{ color: "#7a6a8a" }}>
                Thank you, {personal.firstName}. A RideCloud consultant will review your application and be in touch within 1 business day.
              </p>
              <button onClick={onClose} className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: "#e6531e" }}>Close</button>
            </div>
          ) : (
            <>
              {/* ── Step 1: Personal Info ── */}
              {step === 1 && (
                <div className="space-y-4">
                  <h4 className="font-semibold text-base mb-4" style={{ color: "#2e004f" }}>Personal Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label style={labelStyle}>First Name *</label>
                      <input
                        type="text"
                        value={personal.firstName}
                        onChange={(e) => {
                          setPersonal(p => ({ ...p, firstName: e.target.value }));
                          if (attempted && e.target.value.trim()) setError("");
                        }}
                        className={inputCls}
                        style={{ ...inputStyle, borderColor: attempted && !personal.firstName.trim() ? "#ef4444" : "rgba(46,0,79,0.2)" }}
                        placeholder="John"
                      />
                      {attempted && !personal.firstName.trim() && (
                        <p className="flex items-center gap-1 mt-1 text-xs text-red-500"><AlertCircle className="w-3 h-3 flex-shrink-0" />First name is required.</p>
                      )}
                    </div>
                    <div><label style={labelStyle}>Last Name *</label><input type="text" value={personal.lastName} onChange={(e) => setPersonal(p => ({ ...p, lastName: e.target.value }))} className={inputCls} style={inputStyle} placeholder="Smith" /></div>
                  </div>
                  <div><label style={labelStyle}>Email Address *</label><input type="email" value={personal.email} onChange={(e) => setPersonal(p => ({ ...p, email: e.target.value }))} className={inputCls} style={inputStyle} placeholder="john@example.com" /></div>
                  <div><label style={labelStyle}>Phone Number *</label><input type="tel" value={personal.phone} onChange={(e) => setPersonal(p => ({ ...p, phone: e.target.value }))} className={inputCls} style={inputStyle} placeholder="082 000 0000" /></div>
                  <div>
                    <label style={labelStyle}>How should we reach you? *</label>
                    <div className="grid grid-cols-3 gap-2 mt-1">
                      {(["Phone Call", "WhatsApp", "Email"] as const).map((opt) => (
                        <button key={opt} type="button" onClick={() => setPersonal(p => ({ ...p, contactMethod: opt }))}
                          className="py-2 px-3 rounded-lg text-sm font-medium border transition-all"
                          style={{ backgroundColor: personal.contactMethod === opt ? "#e6531e" : "#fff", color: personal.contactMethod === opt ? "#fff" : "#2e004f", borderColor: personal.contactMethod === opt ? "#e6531e" : "rgba(46,0,79,0.2)" }}>
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label style={labelStyle}>SA ID Number *</label>
                      <input
                        type="text" inputMode="numeric"
                        value={personal.idNumber}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "").slice(0, 13);
                          const dob = val.length >= 6 ? dobFromSAID(val) : personal.dateOfBirth;
                          const liveErr = val.length === 13 ? validateSAID(val).error : "";
                          setIdError(liveErr);
                          setPersonal(p => ({ ...p, idNumber: val, dateOfBirth: dob }));
                        }}
                        className={inputCls}
                        style={{ ...inputStyle, borderColor: idError ? "#ef4444" : personal.idNumber.length === 13 && !idError ? "#16a34a" : "rgba(46,0,79,0.2)" }}
                        placeholder="8001015009087"  maxLength={13}
                      />
                      {idError && <p className="flex items-center gap-1 mt-1 text-xs text-red-500"><AlertCircle className="w-3 h-3 flex-shrink-0" />{idError}</p>}
                      {personal.idNumber.length === 13 && !idError && <p className="flex items-center gap-1 mt-1 text-xs" style={{ color: "#16a34a" }}><Check className="w-3 h-3 flex-shrink-0" />Valid SA ID number</p>}
                    </div>
                    <div>
                      <label style={labelStyle}>Date of Birth <span className="font-normal" style={{ color: "#9a8aaa" }}>(auto-filled)</span></label>
                      <input
                        type="date" value={personal.dateOfBirth}
                        onChange={(e) => setPersonal(p => ({ ...p, dateOfBirth: e.target.value }))}
                        className={inputCls}
                        style={{ ...inputStyle, backgroundColor: personal.dateOfBirth ? "rgba(22,163,74,0.04)" : "#fff", borderColor: personal.dateOfBirth ? "#16a34a" : "rgba(46,0,79,0.2)" }}
                      />
                      {personal.dateOfBirth && <p className="flex items-center gap-1 mt-1 text-xs" style={{ color: "#16a34a" }}><Check className="w-3 h-3 flex-shrink-0" />Auto-populated from ID</p>}
                    </div>
                  </div>
                  <div><label style={labelStyle}>Residential Address</label><input type="text" value={personal.address} onChange={(e) => setPersonal(p => ({ ...p, address: e.target.value }))} className={inputCls} style={inputStyle} placeholder="123 Main Street, Cape Town, 8001" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label style={labelStyle}>Employment Status</label>
                      <select value={personal.employmentStatus} onChange={(e) => setPersonal(p => ({ ...p, employmentStatus: e.target.value }))} className={inputCls} style={inputStyle}>
                        <option value="">Select...</option>
                        <option>Employed</option><option>Self-Employed</option><option>Contract</option><option>Retired</option><option>Other</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Gross Monthly Income</label>
                      <select value={personal.monthlyIncome} onChange={(e) => setPersonal(p => ({ ...p, monthlyIncome: e.target.value }))} className={inputCls} style={inputStyle}>
                        <option value="">Select range...</option>
                        <option value="Under R10,000">Under R10,000</option>
                        <option value="R10,000 - R25,000">R10,000 - R25,000</option>
                        <option value="R25,000 - R50,000">R25,000 - R50,000</option>
                        <option value="R50,000 - R100,000">R50,000 - R100,000</option>
                        <option value="R100,000 - R250,000">R100,000 - R250,000</option>
                        <option value="Over R250,000">Over R250,000</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Step 2: Documents ── */}
              {step === 2 && (
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-base" style={{ color: "#2e004f" }}>Identity Documents</h4>
                    <p className="text-sm mt-0.5" style={{ color: "#7a6a8a" }}>Upload your SA ID and/or driver's licence. At least one is required to approve your application — both will be needed before delivery.</p>
                    <button
                      type="button"
                      onClick={() => { setDocSkipped(true); setIdFile(null); setLicenceFile(null); setError(""); setStep(3); }}
                      className="flex items-center gap-2 mt-3 px-4 py-2 rounded-xl font-semibold text-sm text-white transition-all"
                      style={{ backgroundColor: "#e6531e" }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#c94318"; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#e6531e"; }}
                    >
                      <SkipForward className="w-4 h-4" />
                      Skip for now
                    </button>
                  </div>

                  {/* ── SA ID upload card ── */}
                  {(() => {
                    const DocCard = ({ docType, file, setFile, mode, setMode, validating, validation, setValidation, fileRef, cameraRef }: {
                      docType: "id" | "licence";
                      file: File | null; setFile: (f: File | null) => void;
                      mode: DocMode; setMode: (m: DocMode) => void;
                      validating: boolean;
                      validation: { ok: boolean; message: string } | null;
                      setValidation: (v: { ok: boolean; message: string } | null) => void;
                      fileRef: React.RefObject<HTMLInputElement | null>;
                      cameraRef: React.RefObject<HTMLInputElement | null>;
                    }) => {
                      const label = docType === "id" ? "SA ID / Smart Card" : "Driver's Licence";
                      const required = docType === "id";
                      return (
                        <div className="rounded-xl overflow-hidden" style={{ border: "1px solid rgba(46,0,79,0.15)" }}>
                          <div className="px-4 py-2.5 flex items-center justify-between" style={{ backgroundColor: "rgba(46,0,79,0.06)" }}>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold" style={{ color: "#2e004f" }}>{label}</span>
                              {required && <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: "rgba(230,83,30,0.12)", color: "#e6531e" }}>Required for approval</span>}
                              {!required && <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: "rgba(46,0,79,0.08)", color: "#7a6a8a" }}>Needed for delivery</span>}
                            </div>
                            {file && (
                              <button type="button" onClick={() => { setFile(null); setValidation(null); }} className="text-xs underline" style={{ color: "#9a8aaa" }}>Remove</button>
                            )}
                          </div>
                          <div className="p-4 space-y-3">
                            {/* Mode toggle */}
                            {!file && (
                              <div className="flex gap-2">
                                {(["upload", "scan"] as DocMode[]).map((m) => (
                                  <button key={m} type="button" onClick={() => setMode(m)}
                                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold border transition-all"
                                    style={mode === m ? { backgroundColor: "#2e004f", color: "#fff", borderColor: "#2e004f" } : { backgroundColor: "#fff", color: "#2e004f", borderColor: "rgba(46,0,79,0.2)" }}
                                  >
                                    {m === "upload" ? <Upload className="w-3.5 h-3.5" /> : <Camera className="w-3.5 h-3.5" />}
                                    {m === "upload" ? "Upload File" : "Scan / Photo"}
                                  </button>
                                ))}
                              </div>
                            )}

                            {/* File uploaded state */}
                            {file ? (
                              <div className="space-y-2">
                                <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg" style={{ backgroundColor: "rgba(22,163,74,0.06)", border: "1px solid rgba(22,163,74,0.2)" }}>
                                  <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium truncate" style={{ color: "#16a34a" }}>{file.name}</p>
                                    <p className="text-xs" style={{ color: "#9a8aaa" }}>{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                                  </div>
                                </div>
                                {validating && (
                                  <div className="flex items-center gap-2 text-xs" style={{ color: "#7a6a8a" }}>
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    Validating document...
                                  </div>
                                )}
                                {!validating && validation && (
                                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs"
                                    style={validation.ok
                                      ? { backgroundColor: "rgba(22,163,74,0.06)", border: "1px solid rgba(22,163,74,0.2)", color: "#16a34a" }
                                      : { backgroundColor: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", color: "#dc2626" }
                                    }
                                  >
                                    {validation.ok ? <Check className="w-3.5 h-3.5 flex-shrink-0" /> : <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />}
                                    {validation.message}
                                  </div>
                                )}
                              </div>
                            ) : mode === "upload" ? (
                              <div
                                className="rounded-lg border-2 border-dashed p-5 text-center cursor-pointer transition-all"
                                style={{ borderColor: "rgba(46,0,79,0.2)", backgroundColor: "rgba(46,0,79,0.02)" }}
                                onClick={() => fileRef.current?.click()}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) { setFile(f); setValidation(null); validateDocument(f, docType); } }}
                              >
                                <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
                                  onChange={(e) => { const f = e.target.files?.[0]; if (f) { setFile(f); setValidation(null); validateDocument(f, docType); } }} />
                                <Upload className="w-6 h-6 mx-auto mb-2" style={{ color: "#9a8aaa" }} />
                                <p className="text-sm font-semibold" style={{ color: "#2e004f" }}>Click to upload or drag & drop</p>
                                <p className="text-xs mt-0.5" style={{ color: "#9a8aaa" }}>PDF, JPG or PNG · Max 10 MB</p>
                              </div>
                            ) : (
                              <div className="rounded-lg border-2 border-dashed p-5 text-center" style={{ borderColor: "rgba(46,0,79,0.2)", backgroundColor: "rgba(46,0,79,0.02)" }}>
                                <Camera className="w-6 h-6 mx-auto mb-2" style={{ color: "#9a8aaa" }} />
                                <p className="text-sm font-semibold mb-1" style={{ color: "#2e004f" }}>Take a photo of your {label}</p>
                                <p className="text-xs mb-3" style={{ color: "#9a8aaa" }}>Ensure the document is flat, well-lit and all text is clearly visible.</p>
                                <label className="inline-block px-4 py-2 rounded-full text-sm font-semibold text-white cursor-pointer" style={{ backgroundColor: "#e6531e" }}>
                                  <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleCameraCapture(docType)} />
                                  Open Camera
                                </label>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    };
                    return (
                      <>
                        <DocCard
                          docType="id" file={idFile} setFile={setIdFile}
                          mode={idMode} setMode={setIdMode}
                          validating={idValidating} validation={idValidation} setValidation={setIdValidation}
                          fileRef={idFileInputRef} cameraRef={idCameraInputRef}
                        />
                        <DocCard
                          docType="licence" file={licenceFile} setFile={setLicenceFile}
                          mode={licenceMode} setMode={setLicenceMode}
                          validating={licenceValidating} validation={licenceValidation} setValidation={setLicenceValidation}
                          fileRef={licenceFileInputRef} cameraRef={licenceCameraInputRef}
                        />
                      </>
                    );
                  })()}

                  <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg" style={{ backgroundColor: "rgba(46,0,79,0.04)" }}>
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#7a6a8a" }} />
                    <p className="text-xs" style={{ color: "#7a6a8a" }}>Your documents are encrypted and stored securely. They will only be used to verify your identity as part of the subscription application process.</p>
                  </div>
                </div>
              )}

              {/* ── Step 3: Consent ── */}
              {step === 3 && (
                <div className="space-y-5">
                  <h4 className="font-semibold text-base" style={{ color: "#2e004f" }}>Credit Check Consent</h4>
                  <p className="text-sm" style={{ color: "#7a6a8a" }}>To process your subscription application, RideCloud will conduct a credit bureau check. By proceeding, you consent to this check being performed.</p>
                  <div className="rounded-xl p-5 space-y-4 text-sm leading-relaxed" style={{ backgroundColor: "rgba(46,0,79,0.03)", border: "1px solid rgba(46,0,79,0.1)", color: "#5a3d70" }}>
                    <p><strong style={{ color: "#2e004f" }}>Credit Bureau Enquiry:</strong> By consenting, you authorise RideCloud (Pty) Ltd to submit your personal information to a registered credit bureau for the purpose of assessing your creditworthiness. This enquiry will appear on your credit report.</p>
                    <p><strong style={{ color: "#2e004f" }}>Data Use:</strong> Your personal information will be processed in accordance with the Protection of Personal Information Act (POPIA). It will only be used to evaluate your application and will not be shared with third parties for marketing purposes without your explicit consent.</p>
                    <p><strong style={{ color: "#2e004f" }}>Right to Withdraw:</strong> You may withdraw this consent at any time by contacting RideCloud directly.</p>
                  </div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={creditConsent} onChange={(e) => setCreditConsent(e.target.checked)} className="mt-0.5 w-4 h-4 accent-orange-500 flex-shrink-0" />
                    <span className="text-sm" style={{ color: "#2e004f" }}><strong>I consent to a credit bureau check</strong> being conducted on my behalf by RideCloud (Pty) Ltd. *</span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={marketingConsent} onChange={(e) => setMarketingConsent(e.target.checked)} className="mt-0.5 w-4 h-4 accent-orange-500 flex-shrink-0" />
                    <span className="text-sm" style={{ color: "#5a3d70" }}>I agree to receive marketing communications from RideCloud. (Optional)</span>
                  </label>
                </div>
              )}

              {/* ── Step 4: Review ── */}
              {step === 4 && (
                <div className="space-y-5">
                  <h4 className="font-semibold text-base" style={{ color: "#2e004f" }}>Review Your Application</h4>
                  <div className="rounded-xl p-4" style={{ backgroundColor: "rgba(230,83,30,0.06)", border: "1px solid rgba(230,83,30,0.2)" }}>
                    <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: "#e6531e" }}>Vehicle & Plan</p>
                    <p className="text-sm font-semibold" style={{ color: "#2e004f" }}>{vehicleName}</p>
                    <p className="text-sm" style={{ color: "#7a6a8a" }}>{term}-month · {kmPack.toLocaleString()} km/month · {additionalDrivers} additional driver(s) · Delivery: {includeDelivery ? "Yes" : "No"}</p>
                    <p className="text-sm font-bold mt-1" style={{ color: "#e6531e" }}>R{totalMonthly.toLocaleString()} pm</p>
                  </div>
                  <div className="rounded-xl overflow-hidden" style={{ border: "1px solid rgba(46,0,79,0.1)" }}>
                    <div className="px-4 py-2.5" style={{ backgroundColor: "rgba(46,0,79,0.06)" }}>
                      <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#5a3d70" }}>Personal Details</p>
                    </div>
                    <div className="px-4 py-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      {[
                        ["Name", `${personal.firstName} ${personal.lastName}`],
                        ["Email", personal.email],
                        ["Phone", personal.phone],
                        ["ID Number", personal.idNumber],
                        ["Employment", personal.employmentStatus || "—"],
                        ["Monthly Income", personal.monthlyIncome || "—"],
                      ].map(([k, v]) => (
                        <div key={k}><p className="text-xs" style={{ color: "#9a8aaa" }}>{k}</p><p className="font-medium" style={{ color: "#2e004f" }}>{v}</p></div>
                      ))}
                    </div>
                  </div>
                  {/* Document summary */}
                  {docSkipped ? (
                    <div className="flex items-center gap-2 text-sm" style={{ color: "#9a8aaa" }}>
                      <SkipForward className="w-4 h-4 flex-shrink-0" />
                      Document upload skipped — you can provide this later.
                    </div>
                  ) : (idFile || licenceFile) ? (
                    <div className="space-y-1.5">
                      {idFile && (
                        <div className="flex items-center gap-2 text-sm" style={{ color: "#2e004f" }}>
                          <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                          SA ID: {idFile.name}
                        </div>
                      )}
                      {licenceFile && (
                        <div className="flex items-center gap-2 text-sm" style={{ color: "#2e004f" }}>
                          <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                          Driver's Licence: {licenceFile.name}
                        </div>
                      )}
                      {!idFile && (
                        <div className="flex items-center gap-2 text-sm" style={{ color: "#9a8aaa" }}>
                          <AlertCircle className="w-4 h-4 flex-shrink-0" />
                          SA ID not uploaded — required before delivery.
                        </div>
                      )}
                      {!licenceFile && (
                        <div className="flex items-center gap-2 text-sm" style={{ color: "#9a8aaa" }}>
                          <AlertCircle className="w-4 h-4 flex-shrink-0" />
                          Driver's Licence not uploaded — required before delivery.
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm" style={{ color: "#9a8aaa" }}>
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      No documents uploaded — you can provide these later.
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm" style={{ color: "#16a34a" }}>
                    <Check className="w-4 h-4" />Credit check consent provided
                  </div>
                </div>
              )}

              {error && (
                <div className="flex items-center gap-2 mt-3 text-sm text-red-500">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />{error}
                </div>
              )}

              <div className="flex gap-3 mt-6">
                {step > 1 && (
                  <button type="button" onClick={() => { setError(""); setStep((s) => (s - 1) as ApplyStep); }}
                    className="flex-1 py-3 rounded-xl font-semibold text-sm transition-all"
                    style={{ border: "2px solid rgba(46,0,79,0.2)", color: "#2e004f", backgroundColor: "#fff" }}>
                    Back
                  </button>
                )}
                {step < 4 ? (
                  <button type="button" onClick={handleNext}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm text-white"
                    style={{ backgroundColor: "#e6531e" }}>
                    Continue <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button type="button" onClick={handleSubmit} disabled={submitting}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm text-white"
                    style={{ backgroundColor: submitting ? "#9a8aaa" : "#2e004f" }}>
                    {submitting ? <><Loader2 className="w-4 h-4 animate-spin" />Submitting...</> : <><Check className="w-4 h-4" />Submit Application</>}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── VehicleDetail Page ───────────────────────────────────────────────────────
export default function VehicleDetail() {
  const params = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [loading, setLoading] = useState(true);

  const [selectedTerm, setSelectedTerm] = useState<Term>(36);
  const [kmPack, setKmPack] = useState<KmPack>(1500);
  const [additionalDrivers, setAdditionalDrivers] = useState(0);
  const [includeDelivery, setIncludeDelivery] = useState(false);
  const [activeModal, setActiveModal] = useState<Modal>("none");

  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        const res = await fetch("/api/vehicles");
        const data = await res.json();
        const found = (data.vehicles as Vehicle[]).find(
          (v) => String(v.id) === String(params.id) || String(v.zohoId) === String(params.id)
        );
        if (found) setVehicle(found);
        else navigate("/");
      } catch { navigate("/"); }
      finally { setLoading(false); }
    };
    fetchVehicle();
  }, [params.id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#f8f7fa" }}>
        <div className="flex items-center gap-3" style={{ color: "#2e004f" }}><Loader2 className="w-6 h-6 animate-spin" /><span className="font-medium">Loading vehicle details...</span></div>
      </div>
    );
  }
  if (!vehicle) return null;

  // Available km packs for this vehicle
  const availableKmPacks: KmPack[] = vehicle.availableKmPacks
    ? (vehicle.availableKmPacks.filter((k) => [1500, 2000, 2500].includes(k)) as KmPack[])
    : [1500, 2000, 2500];

  // Auto-correct kmPack if not available for this vehicle
  const effectiveKmPack: KmPack = availableKmPacks.includes(kmPack) ? kmPack : availableKmPacks[0];

  // Available terms for the selected km pack
  const availableTerms = getAvailableTermsForKm(vehicle, effectiveKmPack);
  const effectiveTerm: Term = availableTerms.includes(selectedTerm) ? selectedTerm : availableTerms[0];

  // Rate directly from kmPricing — no surcharge needed (price already includes km)
  const baseRate = getRateForKmAndTerm(vehicle, effectiveKmPack, effectiveTerm) ?? vehicle.fromPrice ?? 0;
  const driverFee = additionalDrivers * ADDITIONAL_DRIVER_FEE;
  const totalMonthly = baseRate + driverFee;
  // Subscription deposit = 2× monthly instalment
  const subscriptionDeposit = totalMonthly * 2;
  const totalUpfront = subscriptionDeposit + 5000 + (includeDelivery ? DELIVERY_FEE : 0);
  // Use RRP from Zoho for bank finance calculations; fall back to rate12*60 estimate if not available
  const rrpForFinance = (vehicle as any).rrp ?? ((vehicle.rate12 ?? vehicle.rate24 ?? vehicle.rate36 ?? vehicle.fromPrice ?? 0) as number) * 60;
  const finance = getBankFinanceEstimate(rrpForFinance, effectiveKmPack);
  // Term-based depreciation: steeper in year 1, reducing over time
  const deprRateByTerm: Record<Term, number> = { 12: 0.0167, 24: 0.0146, 36: 0.012 };
  const monthlyDepreciation = Math.round(rrpForFinance * deprRateByTerm[effectiveTerm]);
  const vehicleName = `${vehicle.make} ${vehicle.model} ${vehicle.variant}`;
  const terms: Term[] = availableTerms;

  const btnBase = "flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl font-semibold transition-all";

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#f8f7fa" }}>
      <Navbar />

      {/* Back nav */}
      <div className="container pt-6 pb-2">
        <button onClick={() => navigate("/vehicles")} className="flex items-center gap-1.5 text-sm font-medium transition-colors" style={{ color: "#5a3d70" }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#2e004f"; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#5a3d70"; }}>
          <ChevronLeft className="w-4 h-4" />Back to Catalogue
        </button>
      </div>

      <div className="container pb-20">
        {/* ── 3-column hero layout ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4 mb-10">

          {/* Column 1: Vehicle image + specs */}
          <div className="flex flex-col gap-4">
            <div className="rounded-xl overflow-hidden" style={{ border: "1px solid rgba(0,0,0,0.1)" }}>
              <img src={vehicle.image} alt={vehicleName} className="w-full object-cover" style={{ height: "220px" }}
                onError={(e) => { (e.target as HTMLImageElement).src = "/vehicles/polo-vivo.jpg"; }} />
            </div>
            <div>
              <h1 className="text-2xl font-bold mb-0.5" style={{ color: "#2e004f" }}>{vehicle.make} {vehicle.model}</h1>
              <p className="text-base mb-3" style={{ color: "#7a6a8a" }}>{vehicle.variant}</p>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {[vehicle.type, vehicle.transmission, vehicle.fuel, `${vehicle.seats} Seats`].map((spec) => (
                  <span key={spec} className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: "rgba(46,0,79,0.08)", color: "#2e004f" }}>{spec}</span>
                ))}
              </div>
              <p className="text-xs mb-2" style={{ color: "#9a8aaa" }}>
                Available Terms &nbsp;
                {terms.map((t, i) => <span key={t} style={{ color: "#2e004f", fontWeight: 600 }}>{t}{i < terms.length - 1 ? " · " : ""}</span>)}
              </p>
              <p className="text-sm leading-relaxed mb-3" style={{ color: "#5a3d70" }}>{vehicle.desc}</p>
              <p className="text-base font-bold" style={{ color: "#2e004f" }}>From R{vehicle.fromPrice.toLocaleString()} pm</p>
            </div>
          </div>

          {/* Column 2: Options */}
          <div className="rounded-xl p-5" style={{ backgroundColor: "#ffffff", border: "1px solid rgba(46,0,79,0.1)" }}>
            <h2 className="text-lg font-bold mb-1" style={{ color: "#2e004f" }}>Options</h2>
            <p className="text-xs mb-5" style={{ color: "#9a8aaa" }}>Select a term and extras to estimate your costs.</p>

            {/* Term selector */}
            <div className="mb-5">
              <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "#5a3d70" }}>Term (months)</label>
              <div className="grid grid-cols-5 gap-1.5">
                {terms.map((t) => (
                  <button key={t} onClick={() => setSelectedTerm(t)} className="py-2 rounded-lg text-sm font-semibold transition-all"
                    style={effectiveTerm === t ? { backgroundColor: "#2e004f", color: "#fff" } : { backgroundColor: "rgba(46,0,79,0.06)", color: "#2e004f" }}>
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* KM pack */}
            <div className="mb-5">
              <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "#5a3d70" }}>KM packs (choose one)</label>
              <div className="space-y-2">
                {availableKmPacks.map((km) => {
                  const rateForKm = getRateForKmAndTerm(vehicle, km, effectiveTerm);
                  const baseKmRate = getRateForKmAndTerm(vehicle, availableKmPacks[0], effectiveTerm);
                  const diff = rateForKm && baseKmRate ? rateForKm - baseKmRate : 0;
                  return (
                    <label key={km} className="flex items-center justify-between px-4 py-3 rounded-lg cursor-pointer transition-all"
                      style={effectiveKmPack === km ? { backgroundColor: "rgba(46,0,79,0.06)", border: "1.5px solid #2e004f" } : { backgroundColor: "#fff", border: "1.5px solid rgba(46,0,79,0.15)" }}>
                      <div className="flex items-center gap-3">
                        <input type="radio" checked={effectiveKmPack === km} onChange={() => setKmPack(km)} className="accent-purple-900" />
                        <span className="text-sm font-medium" style={{ color: "#2e004f" }}>{km.toLocaleString()} km/month</span>
                      </div>
                      {rateForKm ? (
                        <span className="text-xs font-semibold" style={{ color: diff > 0 ? "#e6531e" : "#2e004f" }}>
                          R{rateForKm.toLocaleString()} pm
                        </span>
                      ) : (
                        <span className="text-xs" style={{ color: "#9a8aaa" }}>N/A this term</span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Monthly extras */}
            <div className="mb-5">
              <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "#5a3d70" }}>Monthly extras</label>
              <div className="px-4 py-3 rounded-lg" style={{ border: "1.5px solid rgba(46,0,79,0.15)" }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium" style={{ color: "#2e004f" }}>Additional drivers</p>
                    {additionalDrivers > 0 && <p className="text-xs" style={{ color: "#e6531e" }}>+R{(additionalDrivers * ADDITIONAL_DRIVER_FEE).toLocaleString()} pm</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setAdditionalDrivers(Math.max(0, additionalDrivers - 1))} className="w-7 h-7 rounded-full flex items-center justify-center transition-all" style={{ backgroundColor: "rgba(46,0,79,0.08)", color: "#2e004f" }}><Minus className="w-3.5 h-3.5" /></button>
                    <span className="w-6 text-center text-sm font-bold" style={{ color: "#2e004f" }}>{additionalDrivers}</span>
                    <button onClick={() => setAdditionalDrivers(Math.min(3, additionalDrivers + 1))} className="w-7 h-7 rounded-full flex items-center justify-center transition-all" style={{ backgroundColor: "rgba(46,0,79,0.08)", color: "#2e004f" }}><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>
            </div>

            {/* Once-off extras */}
            <div className="mb-6">
              <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "#5a3d70" }}>Once-off</label>
              <label className="flex items-center justify-between px-4 py-3 rounded-lg cursor-pointer transition-all"
                style={includeDelivery ? { backgroundColor: "rgba(46,0,79,0.06)", border: "1.5px solid #2e004f" } : { backgroundColor: "#fff", border: "1.5px solid rgba(46,0,79,0.15)" }}>
                <div className="flex items-center gap-3">
                  <input type="checkbox" checked={includeDelivery} onChange={(e) => setIncludeDelivery(e.target.checked)} className="accent-purple-900" />
                  <span className="text-sm font-medium" style={{ color: "#2e004f" }}>Doorstep Delivery</span>
                </div>
                <span className="text-xs font-semibold" style={{ color: "#e6531e" }}>+R{DELIVERY_FEE.toLocaleString()}</span>
              </label>
            </div>

            {/* CTA buttons */}
            <div className="flex flex-col gap-2">
              <button onClick={() => setActiveModal("contact")} className={`${btnBase} w-full`}
                style={{ border: "2px solid #2e004f", color: "#2e004f", backgroundColor: "#fff" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#2e004f"; (e.currentTarget as HTMLButtonElement).style.color = "#fff"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#fff"; (e.currentTarget as HTMLButtonElement).style.color = "#2e004f"; }}>
                <MessageCircle className="w-4 h-4" />Contact Me
              </button>
              <button onClick={() => setActiveModal("apply")} className={`${btnBase} w-full text-white`}
                style={{ backgroundColor: "#e6531e" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#c9431a"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#e6531e"; }}>
                <ClipboardList className="w-4 h-4" />Apply Now
              </button>
            </div>
          </div>

          {/* Column 3: Cost Breakdown */}
          <div className="flex flex-col gap-5">
            <h2 className="text-lg font-bold" style={{ color: "#2e004f" }}>Cost Breakdown</h2>

            {/* Monthly table */}
            <div className="rounded-xl overflow-hidden" style={{ border: "1px solid rgba(0,0,0,0.1)" }}>
              <div className="grid grid-cols-3 px-4 py-2.5" style={{ backgroundColor: "#2e004f" }}>
                <span className="text-xs font-bold text-white uppercase tracking-wider">Monthly</span>
                <span className="text-xs font-bold text-right" style={{ color: "#f97316" }}>Subscription</span>
                <span className="text-xs font-bold text-right text-white/60">Bank Finance</span>
              </div>
              {[
                { label: "Vehicle",     sub: `R${totalMonthly.toLocaleString()}`,        bank: `R${finance.monthlyVehicle.toLocaleString()}` },
                { label: "Insurance",   sub: "incl",                                      bank: `R${finance.insurance.toLocaleString()}` },
                { label: "Tracker",     sub: "incl",                                      bank: `R${finance.tracker.toLocaleString()}` },
                { label: "Tyres",       sub: "incl",                                      bank: `R${finance.tyres.toLocaleString()}` },
                { label: "Maintenance", sub: "incl",                                      bank: `R${finance.maintenance.toLocaleString()}` },
                { label: "Licensing",   sub: "incl",                                      bank: `R${finance.licensing.toLocaleString()}` },
              ].map((row, i) => (
                <div key={row.label} className="grid grid-cols-3 px-4 py-2" style={{ backgroundColor: i % 2 === 0 ? "#fff" : "rgba(46,0,79,0.02)" }}>
                  <span className="text-xs" style={{ color: "#5a3d70" }}>{row.label}</span>
                  <span className="text-xs text-right font-semibold" style={{ color: "#e6531e" }}>{row.sub}</span>
                  <span className="text-xs text-right" style={{ color: "#7a6a8a" }}>{row.bank}</span>
                </div>
              ))}
              <div className="grid grid-cols-3 px-4 py-2.5" style={{ backgroundColor: "rgba(46,0,79,0.06)", borderTop: "1.5px solid rgba(46,0,79,0.12)" }}>
                <span className="text-xs font-bold" style={{ color: "#2e004f" }}>Total Payment</span>
                <span className="text-sm font-bold text-right" style={{ color: "#e6531e" }}>R{totalMonthly.toLocaleString()}</span>
                <span className="text-sm font-bold text-right" style={{ color: "#5a3d70" }}>R{finance.totalMonthly.toLocaleString()}</span>
              </div>
              <div className="grid grid-cols-3 px-4 py-2" style={{ backgroundColor: "#fff" }}>
                <span className="text-xs" style={{ color: "#5a3d70" }}>Depreciation</span>
                <span className="text-xs text-right font-semibold" style={{ color: "#e6531e" }}>incl</span>
                <span className="text-xs text-right" style={{ color: "#7a6a8a" }}>R{monthlyDepreciation.toLocaleString()}</span>
              </div>
              <div className="grid grid-cols-3 px-4 py-3" style={{ backgroundColor: "rgba(46,0,79,0.04)", borderTop: "1.5px solid rgba(46,0,79,0.12)" }}>
                <span className="text-sm font-bold" style={{ color: "#2e004f" }}>Total Cost</span>
                <span className="text-base font-bold text-right px-1 rounded" style={{ color: "#fff", backgroundColor: "#e6531e" }}>R{totalMonthly.toLocaleString()} ▶</span>
                <span className="text-base font-bold text-right" style={{ color: "#5a3d70" }}>R{(finance.totalMonthly + monthlyDepreciation).toLocaleString()}</span>
              </div>
            </div>

            {/* Upfront table */}
            <div className="rounded-xl overflow-hidden" style={{ border: "1px solid rgba(0,0,0,0.1)" }}>
              <div className="grid grid-cols-3 px-4 py-2.5" style={{ backgroundColor: "#2e004f" }}>
                <span className="text-xs font-bold text-white uppercase tracking-wider">Up Front</span>
                <span className="text-xs font-bold text-right" style={{ color: "#f97316" }}>Subscription</span>
                <span className="text-xs font-bold text-right text-white/60">Bank Finance</span>
              </div>
              {/* Deposit row — special treatment for refundable badge */}
              <div className="grid grid-cols-3 px-4 py-2" style={{ backgroundColor: "#fff" }}>
                <span className="text-xs" style={{ color: "#5a3d70" }}>Deposit</span>
                <div className="flex flex-col items-end gap-0.5">
                  <span className="text-xs font-semibold" style={{ color: "#e6531e" }}>R{subscriptionDeposit.toLocaleString()}</span>
                  <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: "#dcfce7", color: "#16a34a", fontSize: "0.6rem", lineHeight: 1.2 }}>Refundable</span>
                </div>
                <div className="flex flex-col items-end gap-0.5">
                  <span className="text-xs" style={{ color: "#7a6a8a" }}>R{finance.deposit.toLocaleString()}</span>
                  <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: "#f3f4f6", color: "#9ca3af", fontSize: "0.6rem", lineHeight: 1.2 }}>Non-refundable</span>
                </div>
              </div>
              {[
                { label: "Initiation Fee", sub: "R5 000",                            bank: "R5 000" },
                { label: "Delivery Fee",   sub: includeDelivery ? `R${DELIVERY_FEE.toLocaleString()}` : "R0", bank: "R0" },
                { label: "On the Road",    sub: "incl",                              bank: "R6 000" },
              ].map((row, i) => (
                <div key={row.label} className="grid grid-cols-3 px-4 py-2" style={{ backgroundColor: i % 2 === 0 ? "rgba(46,0,79,0.02)" : "#fff" }}>
                  <span className="text-xs" style={{ color: "#5a3d70" }}>{row.label}</span>
                  <span className="text-xs text-right font-semibold" style={{ color: "#e6531e" }}>{row.sub}</span>
                  <span className="text-xs text-right" style={{ color: "#7a6a8a" }}>{row.bank}</span>
                </div>
              ))}
              <div className="grid grid-cols-3 px-4 py-3" style={{ backgroundColor: "rgba(46,0,79,0.04)", borderTop: "1.5px solid rgba(46,0,79,0.12)" }}>
                <span className="text-sm font-bold" style={{ color: "#2e004f" }}>Total Upfront</span>
                <span className="text-base font-bold text-right px-1 rounded" style={{ color: "#fff", backgroundColor: "#e6531e" }}>R{totalUpfront.toLocaleString()} ▶</span>
                <span className="text-base font-bold text-right" style={{ color: "#5a3d70" }}>R{finance.totalUpfront.toLocaleString()}</span>
              </div>
            </div>

            <p className="text-xs" style={{ color: "#9a8aaa" }}>* Bank finance estimates based on 10% deposit, 60-month term at prime + 2.5% (~12.75% p.a.). Actual figures will vary.</p>
            <p className="text-xs" style={{ color: "#9a8aaa" }}>** RideCloud security deposit is fully refundable at the end of your subscription term, subject to vehicle condition assessment.</p>
          </div>
        </div>

        {/* ── What's Included ── */}
        <div className="mb-10">
          <h2 className="text-2xl font-bold mb-2" style={{ color: "#2e004f" }}>What's Included</h2>
          <p className="text-sm mb-6" style={{ color: "#7a6a8a" }}>Every RideCloud subscription includes the following at no extra cost.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {inclusions.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="rounded-xl p-5" style={{ backgroundColor: "#ffffff", border: "1px solid rgba(46,0,79,0.1)" }}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: "rgba(46,0,79,0.08)" }}><Icon className="w-5 h-5" style={{ color: "#2e004f" }} /></div>
                <h4 className="text-sm font-semibold mb-1.5" style={{ color: "#2e004f" }}>{title}</h4>
                <p className="text-xs leading-relaxed" style={{ color: "#7a6a8a" }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Subscription vs Finance ── */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6" style={{ color: "#2e004f" }}>Traditional Finance vs Subscription</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-xl p-6" style={{ backgroundColor: "#ffffff", border: "1px solid rgba(0,0,0,0.1)" }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(239,68,68,0.1)" }}><X className="w-4 h-4 text-red-500" /></div>
                <h3 className="font-bold" style={{ color: "#2e004f" }}>Traditional Bank Finance</h3>
              </div>
              <ul className="space-y-3">{bankCons.map((con) => (<li key={con} className="flex items-start gap-2.5 text-sm" style={{ color: "#5a3d70" }}><X className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />{con}</li>))}</ul>
            </div>
            <div className="rounded-xl p-6" style={{ backgroundColor: "rgba(46,0,79,0.04)", border: "2px solid rgba(46,0,79,0.2)" }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(22,163,74,0.1)" }}><Check className="w-4 h-4 text-green-600" /></div>
                <h3 className="font-bold" style={{ color: "#2e004f" }}>RideCloud Subscription</h3>
              </div>
              <ul className="space-y-3">{subscriptionPros.map((pro) => (<li key={pro} className="flex items-start gap-2.5 text-sm" style={{ color: "#2e004f" }}><Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />{pro}</li>))}</ul>
            </div>
          </div>
        </div>

        {/* ── Bottom CTA ── */}
        <div className="text-center py-10 rounded-2xl" style={{ backgroundColor: "#2e004f" }}>
          <h3 className="text-2xl font-bold text-white mb-2">Ready to get started?</h3>
          <p className="text-white/60 text-sm mb-6">Have a question first, or ready to apply? We're here to help.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => setActiveModal("contact")} className={`${btnBase}`}
              style={{ border: "2px solid rgba(255,255,255,0.3)", color: "#fff", backgroundColor: "transparent" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(255,255,255,0.1)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "transparent"; }}>
              <MessageCircle className="w-4 h-4" />Contact Me
            </button>
            <button onClick={() => setActiveModal("apply")} className={`${btnBase} text-white`}
              style={{ backgroundColor: "#e6531e" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#c9431a"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#e6531e"; }}>
              <ClipboardList className="w-4 h-4" />Apply Now
            </button>
          </div>
        </div>
      </div>

      <Footer />

      {activeModal === "contact" && vehicle && (
        <ContactModal vehicle={vehicle} term={effectiveTerm} rateForTerm={totalMonthly} onClose={() => setActiveModal("none")} />
      )}
      {activeModal === "apply" && vehicle && (
        <ApplyModal vehicle={vehicle} term={effectiveTerm} rateForTerm={baseRate} kmPack={effectiveKmPack} additionalDrivers={additionalDrivers} includeDelivery={includeDelivery} onClose={() => setActiveModal("none")} />
      )}
    </div>
  );
}
